import React, { useState, useContext } from 'react';
import { useVocabularyTraining } from '../../hooks/useVocabularyTraining';
import { useAuth } from '../../contexts/AuthContext';
import { UserContext } from '../../store/useStore';
import { ActivityType } from '../../types/vocabulary';
import PointsFeedback from '../common/PointsFeedback';
import {
    ReviewCard,
    SentenceFormation,
    ListenToWrite,
    Translation,
    ImageLinking,
    SpeakWord,
    SpeakPhrase,
    Synonyms,
    ListenAndArrange,
    FillInTheBlanks,
    Matching
} from './activities/Activities';
import { TrainingSummary } from './TrainingSummary';
import { TrainingReview } from './TrainingReview';
import { Loader2, Trophy, ArrowLeft, Brain, BookmarkPlus, Check } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getUserStats, getTrainingWeekActivity } from '../../services/vocabularyService';
import { themes } from '../../utils/themes';

interface VocabularyTrainingProps {
  themeId?: string;
}

const VocabularyTraining: React.FC<VocabularyTrainingProps> = ({ themeId }) => {
    const { currentUser } = useAuth();
    const { getWordsPaginated, getWordsFromTheme } = useContext(UserContext);
    const navigate = useNavigate();
    const [showPoints, setShowPoints] = useState(false);
    const [pointsEarned, setPointsEarned] = useState(0);
    const [streak, setStreak] = useState(0);
    const [previousStreak, setPreviousStreak] = useState(0);
    const [weekHistory, setWeekHistory] = useState<boolean[]>([]);
    const [showReview, setShowReview] = useState(false);
    const { addWord, updateWordProgress } = useContext(UserContext);
    const [savedWordsStatus, setSavedWordsStatus] = useState<Record<string, boolean>>({});

    const themeName = themeId ? themes.find(t => t.id === themeId)?.name : null;

    const {
        loading,
        session,
        currentWord,
        currentActivity,
        progress,
        handleActivityComplete,
        handleUpdateWord,
        completed,
        restartTraining,
        results
    } = useVocabularyTraining(currentUser?.uid, currentUser, themeId);

    // Load initial streak and week history when component mounts
    React.useEffect(() => {
        if (currentUser?.uid) {
            getUserStats(currentUser.uid).then(stats => {
                const initialStreak = stats.streakCount || 0;
                // Set both to initial streak (before training starts)
                setPreviousStreak(initialStreak);
                setStreak(initialStreak);
                
                // Get week history for calendar
                const history = getTrainingWeekActivity(stats.dailyPoints || {}, currentUser.uid);
                setWeekHistory(history);
            });
        }
    }, [currentUser?.uid]);

    // Update streak when training completes
    React.useEffect(() => {
        if (completed && currentUser?.uid) {
            // Longer delay to ensure database writes complete and stats are updated
            const updateStats = async () => {
                // Wait a bit longer to ensure all async updates complete
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Fetch updated stats
                const stats = await getUserStats(currentUser.uid);
                const newStreak = stats.streakCount || 0;
                
                // Update streak (previousStreak should already be set from initial load)
                setStreak(newStreak);
                
                // Update week history (mark today as completed)
                // Ensure today is marked as completed in localStorage (use same key format as markDailyTrainingCompleted)
                const today = new Date().toISOString().split('T')[0];
                const completionKey = `dailyChallengeCompleted_${currentUser.uid}_${today}`;
                if (localStorage.getItem(completionKey) !== 'true') {
                    localStorage.setItem(completionKey, 'true');
                }
                
                const history = getTrainingWeekActivity(stats.dailyPoints || {}, currentUser.uid);
                setWeekHistory(history);
                
                // Refresh saved words from Firebase after theme training completes
                if (themeId && themeName) {
                    // Refresh words for this specific theme
                    getWordsFromTheme(themeName);
                }
                // Always refresh the main savedWords list to include any new words
                getWordsPaginated(false);
            };
            
            updateStats().catch(console.error);
        }
    }, [completed, currentUser?.uid, themeId, themeName, getWordsPaginated, getWordsFromTheme]);

    const onActivityComplete = async (success: boolean, userAnswer?: string, correctAnswer?: string, score?: number) => {
        if (success) {
            let points = 10;
            if (score !== undefined) {
                if (score >= 90) {
                    points = 10;
                } else if (score >= 50) {
                    points = Math.floor((score / 100) * 10);
                }
            }
            if (points > 0) {
                setPointsEarned(points);
                setShowPoints(true);
            }
            if (currentWord) {
                updateWordProgress(currentWord.word, themeName);
            }
        }
        await handleActivityComplete(success, userAnswer, correctAnswer);
    };

    const handleSaveCurrentWord = async () => {
        if (!currentWord || !currentWord.isUnsaved) return;
        try {
            await addWord(currentWord.word);
            setSavedWordsStatus(prev => ({ ...prev, [currentWord.word]: true }));
        } catch (error) {
            console.error('Error saving word:', error);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
        );
    }

    if (completed) {
        if (showReview) {
            return (
                <TrainingReview
                    results={results}
                    onBack={() => setShowReview(false)}
                />
            );
        }

        return (
            <TrainingSummary
                results={results}
                onReview={() => setShowReview(true)}
                onContinue={() => {
                    restartTraining();
                    setShowReview(false);
                }}
                onGoToDashboard={!themeId ? () => navigate('/dashboard') : undefined}
                onGoToThemes={themeId ? () => navigate('/themes') : undefined}
                onNewRound={() => {
                    restartTraining();
                    setShowReview(false);
                }}
                currentStreak={streak}
                previousStreak={previousStreak}
                weekHistory={weekHistory}
            />
        );
    }

    if (!session || session.length === 0) {
         return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 text-center">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">{themeId ? 'Loading Theme...' : 'No words to practice'}</h1>
                <p className="text-gray-600 mb-6">{themeId ? 'Preparing your session...' : 'Add some words to your vocabulary list first!'}</p>
                {!themeId && (
                  <Link to="/vocabulary" className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                      Go to Vocabulary
                  </Link>
                )}
                {themeId && (
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                )}
            </div>
        );
    }

    // Render current activity
    const renderActivity = () => {
        if (!currentWord || !currentActivity) {
            return (
                <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">Loading activity...</p>
                    <Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" />
                </div>
            );
        }

        const activityKey = `${currentWord.id || currentWord.word}-${progress.current}`;
        const props = {
            word: currentWord,
            onComplete: onActivityComplete,
            onUpdateWord: handleUpdateWord
        };

        switch (currentActivity) {
            case ActivityType.Review: return <ReviewCard key={activityKey} {...props} />;
            case ActivityType.SentenceFormation: return <SentenceFormation key={activityKey} {...props} />;
            case ActivityType.ListenToWrite: return <ListenToWrite key={activityKey} {...props} />;
            case ActivityType.Translation: return <Translation key={activityKey} {...props} />;
            case ActivityType.ImageLinking: return <ImageLinking key={activityKey} {...props} />;
            case ActivityType.SpeakWord: return <SpeakWord key={activityKey} {...props} />;
            case ActivityType.SpeakPhrase: return <SpeakPhrase key={activityKey} {...props} />;
            case ActivityType.Synonyms: return <Synonyms key={activityKey} {...props} />;
            case ActivityType.ListenAndArrange: return <ListenAndArrange key={activityKey} {...props} />;
            case ActivityType.FillInTheBlanks: return <FillInTheBlanks key={activityKey} {...props} />;
            case ActivityType.Matching: return <Matching key={activityKey} {...props} />;
            default: return <div className="text-center py-12 text-gray-500">Unknown Activity</div>;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <PointsFeedback
                show={showPoints}
                points={pointsEarned}
                onComplete={() => setShowPoints(false)}
            />
            {/* Header / Progress */}
            <header className="bg-white border-b px-6 py-4 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto flex items-center gap-4">
                    <Link to={themeId ? "/themes" : "/dashboard"} className="p-2 hover:bg-gray-100 rounded-full">
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </Link>
                    <div className="flex-1">
                        <div className="flex justify-between items-end mb-1">
                            <span className="text-xs font-semibold text-gray-500">
                                {themeId ? (themeName ? themeName.toUpperCase() : 'THEME SESSION') : 'DAILY GOAL'}
                            </span>
                            <div className="flex items-center gap-3">
                                {streak > 0 && (
                                    <div className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
                                        <Brain className="w-3 h-3" />
                                        <span>{streak} Day Streak</span>
                                    </div>
                                )}
                                <span className="text-xs font-semibold text-gray-500">{progress.current} / {progress.total}</span>
                            </div>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-purple-600 transition-all duration-500 ease-out"
                                style={{ width: `${progress.percent}%` }}
                            />
                        </div>
                    </div>
                </div>
            </header>

            {/* Activity Content */}
            <main className="flex-1 flex flex-col items-center justify-center p-6">
                <div className="w-full max-w-4xl">
                     <div className="mb-6 text-center flex flex-col items-center gap-2">
                         <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold tracking-wide uppercase">
                             {currentActivity?.replace(/_/g, ' ')}
                         </span>
                         {currentWord?.isUnsaved && (
                             <button 
                               onClick={handleSaveCurrentWord}
                               disabled={savedWordsStatus[currentWord.word]}
                               className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-wide flex items-center gap-1.5 transition-colors ${
                                   savedWordsStatus[currentWord.word] 
                                   ? 'bg-green-100 text-green-700' 
                                   : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                               }`}
                             >
                                 {savedWordsStatus[currentWord.word] ? (
                                     <>
                                         <Check className="w-3.5 h-3.5" />
                                         Saved to Vocabulary
                                     </>
                                 ) : (
                                     <>
                                         <BookmarkPlus className="w-3.5 h-3.5" />
                                         Save to Learn Later
                                     </>
                                 )}
                             </button>
                         )}
                     </div>
                     {renderActivity()}
                </div>
            </main>
        </div>
    );
};

export default VocabularyTraining;
